import React, { useState } from 'react';

export default function Subscription({ user, darkMode }) {
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'

  const plans = [
    {
      name: 'Advisor',
      price: { monthly: 9.99, annual: 99.99 },
      description: '',
      features: [
        'Generous text chat allowance',
        'Perfect for career planning',
        'Get started with confidence',
        'Ideal for students & early career'
      ],
      stripeLink: {
        monthly: 'https://buy.stripe.com/fZufZh8Ba0bR1V80tM9sk04',
        annual: 'https://buy.stripe.com/fZufZh8Ba0bR1V80tM9sk04'
      },
      popular: false
    },
    {
      name: 'Coach',
      price: { monthly: 14.99, annual: 149.99 },
      description: '',
      features: [
        '3 hours of voice chat per month',
        'Ample text chat included',
        '2 mock interview sessions',
        'Tell your story with confidence',
        'Most popular choice!'
      ],
      stripeLink: {
        monthly: 'https://buy.stripe.com/9B6fZh7x61fV1V8a4m9sk08',
        annual: 'https://buy.stripe.com/9B6fZh7x61fV1V8a4m9sk08'
      },
      popular: true
    },
    {
      name: 'Mentor',
      price: { monthly: 24.99, annual: 249.99 },
      description: '',
      features: [
        '6 hours of voice chat per month',
        'Extensive text chat allowance',
        '4 mock interview sessions',
        'Maximum flexibility',
        'Add-ons available if needed'
      ],
      stripeLink: {
        monthly: 'https://buy.stripe.com/eVqaEXcRqe2H1V8a4m9sk07',
        annual: 'https://buy.stripe.com/eVqaEXcRqe2H1V8a4m9sk07'
      },
      popular: false
    }
  ];

  const handleSubscribe = (plan) => {
    const stripeUrl = plan.stripeLink[billingCycle];
    if (stripeUrl) {
      // Open Stripe checkout in new tab
      window.open(stripeUrl, '_blank');
    } else {
      alert('Payment link not available. Please try again later.');
    }
  };

  const theme = darkMode ? {
    bg: '#0a0f1e',
    cardBg: 'rgba(30, 41, 59, 0.5)',
    text: '#ffffff',
    subtext: '#94a3b8',
    border: 'rgba(148, 163, 184, 0.1)',
    accent: '#6366f1',
    accentHover: '#4f46e5'
  } : {
    bg: '#ffffff',
    cardBg: '#ffffff',
    text: '#1e293b',
    subtext: '#64748b',
    border: 'rgba(226, 232, 240, 0.5)',
    accent: '#6366f1',
    accentHover: '#4f46e5'
  };

  return (
    <div style={{
      minHeight: '100vh',
      padding: '40px 20px',
      background: theme.bg,
      color: theme.text,
      fontFamily: 'Inter, system-ui, sans-serif'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '16px', color: theme.text }}>
            Choose Your Plan
          </h1>
          <p style={{ fontSize: '1.125rem', color: theme.subtext, marginBottom: '32px' }}>
            Select the perfect plan for your career journey
          </p>

          {/* Billing Cycle Toggle */}
          <div style={{
            display: 'inline-flex',
            background: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            borderRadius: '12px',
            padding: '4px',
            gap: '4px'
          }}>
            <button
              onClick={() => setBillingCycle('monthly')}
              style={{
                padding: '8px 20px',
                borderRadius: '8px',
                border: 'none',
                background: billingCycle === 'monthly' ? theme.accent : 'transparent',
                color: billingCycle === 'monthly' ? '#ffffff' : theme.text,
                cursor: 'pointer',
                fontWeight: 500,
                transition: 'all 0.2s ease'
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              style={{
                padding: '8px 20px',
                borderRadius: '8px',
                border: 'none',
                background: billingCycle === 'annual' ? theme.accent : 'transparent',
                color: billingCycle === 'annual' ? '#ffffff' : theme.text,
                cursor: 'pointer',
                fontWeight: 500,
                transition: 'all 0.2s ease'
              }}
            >
              Annual
              <span style={{ marginLeft: '8px', fontSize: '0.875rem', opacity: 0.9 }}>
                (Save 17%)
              </span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '24px',
          marginBottom: '48px'
        }}>
          {plans.map((plan) => (
            <div
              key={plan.name}
              style={{
                position: 'relative',
                padding: '32px',
                borderRadius: '16px',
                background: plan.popular 
                  ? (darkMode ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.05)')
                  : theme.cardBg,
                border: plan.popular 
                  ? `2px solid ${theme.accent}`
                  : `1px solid ${theme.border}`,
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                boxShadow: plan.popular ? `0 8px 24px rgba(99, 102, 241, 0.2)` : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 12px 32px rgba(0, 0, 0, ${darkMode ? '0.3' : '0.1'})`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = plan.popular ? `0 8px 24px rgba(99, 102, 241, 0.2)` : 'none';
              }}
            >
              {plan.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: theme.accent,
                  color: '#ffffff',
                  padding: '4px 16px',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  MOST POPULAR
                </div>
              )}

              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: 700,
                marginBottom: '24px',
                color: theme.text
              }}>
                {plan.name}
              </h2>

              <div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', justifyContent: 'center' }}>
                  <span style={{
                    fontSize: '2.5rem',
                    fontWeight: 800,
                    color: theme.accent
                  }}>
                    ${plan.price[billingCycle].toFixed(2)}
                  </span>
                  <span style={{
                    fontSize: '1rem',
                    color: theme.subtext,
                    fontWeight: 400
                  }}>
                    /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </div>
              </div>

              <div style={{
                textAlign: 'left',
                marginBottom: '32px',
                flex: 1
              }}>
                {plan.features.map((feature, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                    fontSize: '0.95rem',
                    color: theme.subtext,
                    lineHeight: 1.5
                  }}>
                    <span style={{
                      color: theme.accent,
                      marginRight: '8px',
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}>•</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleSubscribe(plan)}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  borderRadius: '12px',
                  border: 'none',
                  background: plan.popular ? theme.accent : (darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                  color: plan.popular ? '#ffffff' : theme.text,
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = plan.popular ? theme.accentHover : (darkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)');
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = plan.popular ? theme.accent : (darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)');
                }}
              >
                  Get Started
              </button>
            </div>
          ))}
        </div>

        {/* Footer Note */}
        <div style={{
          textAlign: 'center',
          padding: '24px',
          borderRadius: '12px',
          background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          color: theme.subtext,
          fontSize: '0.875rem'
        }}>
          <p style={{ margin: 0 }}>
            All plans include a 7-day free trial. Cancel anytime. Secure payment processing by Stripe.
          </p>
        </div>
      </div>
    </div>
  );
}
